import type { StorageAdapter } from './storageAdapter';
import { deserialize, serialize, type PersistedSnapshot } from './serializer';
import { STORAGE_KEYS } from '@/constants/storage';

export interface PersistenceService {
  load(): PersistedSnapshot | null;
  /** Debounced write; multiple calls within `debounceMs` collapse into one. */
  save(snapshot: Omit<PersistedSnapshot, 'version' | 'savedAt'>): void;
  /** Writes immediately (e.g. on `pagehide`). */
  flush(): void;
  clear(): void;
  dispose(): void;
}

export const createPersistenceService = (adapter: StorageAdapter, debounceMs = 500): PersistenceService => {
  let pending: Omit<PersistedSnapshot, 'version' | 'savedAt'> | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const write = () => {
    if (!pending) return;
    adapter.write(STORAGE_KEYS.workspaces, serialize(pending));
    pending = null;
  };

  return {
    load: () => deserialize(adapter.read(STORAGE_KEYS.workspaces)),
    save: (snapshot) => {
      pending = snapshot;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        write();
      }, debounceMs);
    },
    flush: () => {
      if (timer) clearTimeout(timer);
      timer = null;
      write();
    },
    clear: () => {
      adapter.remove(STORAGE_KEYS.workspaces);
    },
    dispose: () => {
      if (timer) clearTimeout(timer);
      timer = null;
      pending = null;
    },
  };
};
